import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Award } from "lucide-react";
import { Page, BrandHeader, CloseBtn } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { BreathingOrb } from "@/components/BreathingOrb";
import { useSessions, useUpdateSession } from "@/lib/store";
import { GROUNDING_TOOLS } from "@/lib/support";

const PRESETS = [
  { key: "calming", label: "Calming", inhale: 4000, hold: 2000, exhale: 6000 },
  { key: "balanced", label: "Balanced", inhale: 4000, hold: 4000, exhale: 4000 },
  { key: "extended", label: "Extended exhale", inhale: 4000, hold: 2000, exhale: 8000 },
];
const SENSES = [
  { n: 5, verb: "see", hint: "Look around and name five things you can see." },
  { n: 4, verb: "feel", hint: "Notice four things you can physically feel." },
  { n: 3, verb: "hear", hint: "Listen for three sounds around you." },
  { n: 2, verb: "smell", hint: "Find two things you can smell." },
  { n: 1, verb: "taste", hint: "Notice one thing you can taste." },
];
const MILESTONES = [1, 5, 10, 25, 50, 100];

export default function Grounding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("id");
  const { data: sessions } = useSessions();
  const { mutate: updateSession } = useUpdateSession();
  const [mode, setMode] = useState(params.get("mode") === "54321" ? "54321" : "box");
  const [presetKey, setPresetKey] = useState("calming");
  const [breaths, setBreaths] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [found, setFound] = useState(0);
  const [celebrate, setCelebrate] = useState(null);
  const preset = PRESETS.find((p) => p.key === presetKey);

  useEffect(() => {
    if (mode !== "box") {
      setStepIdx(0);
      return;
    }
    setBreaths(0);
    const t = setInterval(() => setBreaths((b) => b + 1), preset.inhale + preset.hold + preset.exhale);
    return () => clearInterval(t);
  }, [mode, preset]);

  const back = () => navigate(-1);
  const complete = (label) => {
    const s = sessions?.find((x) => x.id === sid);
    if (s && !s.copingMechanisms.includes(label)) {
      updateSession({ id: s.id, patch: { copingMechanisms: [...s.copingMechanisms, label] } });
      const before = (sessions ?? []).filter((x) => x.copingMechanisms.some((c) => GROUNDING_TOOLS.includes(c))).length;
      const hit = MILESTONES.find((m) => before < m && before + 1 >= m);
      if (hit) return setCelebrate(hit);
    }
    if (s) toast.success("Saved to History — well done");
    back();
  };
  const step = SENSES[stepIdx];
  const isLast = stepIdx === SENSES.length - 1;

  return (
    <Page className="min-h-screen flex flex-col">
      <BrandHeader right={<CloseBtn onClick={back} testId="grounding-close" />} />
      <div className="mt-6 self-center inline-flex rounded-full bg-t-surface3 p-1">
        {["box", "54321"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`grounding-mode-${m}`}
            className={`px-6 h-10 rounded-full text-sm font-semibold transition-colors ${mode === m ? "bg-t-surface text-t-on shadow-sm" : "text-t-muted"}`}>
            {m === "box" ? "Breathe" : "5-4-3-2-1"}
          </button>
        ))}
      </div>
      {mode === "box" ? (
        <div className="flex-1 flex flex-col items-center gap-6 py-8">
          <BreathingOrb size={260} inhale={preset.inhale} hold={preset.hold} exhale={preset.exhale} />
          <p className="text-center text-t-on2 max-w-sm">Follow the orb — breathe in as it grows, hold, and breathe out slowly as it shrinks.</p>
          <div className="text-center"><p className="text-4xl font-bold text-t-brand" data-testid="breath-count">{breaths}</p><p className="text-sm text-t-muted">breaths completed</p></div>
          <div className="flex flex-wrap justify-center gap-2">
            {PRESETS.map((p) => (
              <button key={p.key} onClick={() => setPresetKey(p.key)} data-testid={`preset-${p.key}`}
                className={`px-4 h-10 rounded-full border text-sm font-medium transition-colors ${presetKey === p.key ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface2 text-t-on2 border-t-border"}`}>{p.label}</button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center gap-4 py-8 text-center">
          <div className="flex gap-2">{SENSES.map((s, i) => <span key={s.verb} className={`w-2.5 h-2.5 rounded-full ${i <= stepIdx ? "bg-t-brand" : "bg-t-surface3"}`} />)}</div>
          <p className="text-[96px] leading-none font-bold text-t-on" data-testid="sense-number">{step.n}</p>
          <p className="text-2xl font-semibold text-t-on">things you can <span className="text-t-brand">{step.verb}</span></p>
          <p className="text-t-on2">{step.hint}</p>
          <div className="flex gap-3 mt-4">
            {Array.from({ length: step.n }).map((_, i) => (
              <button key={i} onClick={() => setFound((f) => (i < f ? f : Math.min(step.n, f + 1)))} data-testid={`sense-tap-${i}`}
                className={`w-14 h-14 rounded-full border font-bold transition-colors ${i < found ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface2 text-t-muted border-t-border"}`}>{i + 1}</button>
            ))}
          </div>
          <p className="text-sm text-t-muted">{found < step.n ? "Tap a circle each time you notice one." : "Lovely. Move on when you're ready."}</p>
        </div>
      )}
      <div>
        {mode === "box" ? (
          <Btn className="w-full" label="I feel calmer" onClick={() => complete("Box breathing")} testId="grounding-done" />
        ) : isLast ? (
          <Btn className="w-full" label="Done" onClick={() => complete("5-4-3-2-1 grounding")} testId="grounding-done" />
        ) : (
          <Btn className="w-full" label="Next" onClick={() => { setFound(0); setStepIdx((i) => i + 1); }} testId="grounding-next" />
        )}
      </div>
      {celebrate ? (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-6" data-testid="badge-celebration">
          <div className="bg-t-surface rounded-3xl p-8 max-w-sm text-center tf-rise">
            <span className="mx-auto w-16 h-16 rounded-full bg-t-brand3 text-t-brand grid place-items-center"><Award size={32} /></span>
            <h3 className="mt-4 text-2xl font-bold text-t-on">{celebrate === 1 ? "First grounding!" : `${celebrate} groundings`}</h3>
            <p className="mt-2 text-t-on2">Every time you come back to your body, it gets a little easier.</p>
            <Btn className="w-full mt-6" label="Thank you" onClick={back} testId="badge-done" />
          </div>
        </div>
      ) : null}
    </Page>
  );
}
